import { Button, ButtonGroup, Checkbox, FormControlLabel, Grid, Tooltip } from '@material-ui/core';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { GridColumns, GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import { LegendItem, LegendLabel, LegendQuantile } from '@visx/legend';
import { scaleBand, scaleLinear, scaleOrdinal, scaleQuantile } from '@visx/scale';
import { Bar } from '@visx/shape';
import { timeDays, timeMonths, timeWeek, timeYear } from 'd3-time';
import { timeFormat } from 'd3-time-format';
import moment from 'moment';
import React, { CSSProperties, useState } from 'react';
import { LargeColor, Line2019Color, Line2020Color, ResidentialColor, SMEColor } from '../consts';
import { DailyChange, KeyDate } from '../Data/DataTypes';
import '../styles.css';

interface CalendarProps {
    width: number,
    height: number,
    data: DailyChange[],
    KeyDateData: KeyDate[],
    setStoryLim: Function
}

export function KeyDateCalendar(props: CalendarProps) {
    const CELL_SIZE = 18;
    const [activeDate, setActiveDate] = useState<string>("")
    const [activeDay, setActiveDay] = useState<string>("")
    const [selectedDayData, setselectedDayData] = useState<KeyDate[]>([])
    const [KeyDate2019, setKeyDate2019] = useState<Date>();
    const [showLockdownPeriod, setshowLockdownPeriod] = useState<boolean>(false);

    const [xScale, setxScale] = useState<any>(undefined)
    const [yScale, setyScale] = useState<any>(undefined)

    const leftPad = 50;
    const topPad = 30;
    const graphCanvasHeight = 150;
    const detailCanvasHeight = 500;
    const detailCanvasWidth = 500;
    const xAxisRange: [number, number] = [leftPad, detailCanvasWidth]
    const yAxisRange: [number, number] = [detailCanvasHeight - topPad, topPad]

    const marketColorScale = scaleOrdinal({
        domain: ["Residential", "Commercial", "Large"],
        range: ['#6c5efb', '#c998ff', '#a44afe'],
    });

    function pathMonth(t0: any) {
        const d0 = t0.getDay();
        const w0 = timeWeek.count(timeYear(t0), t0);
        const t1 = new Date(t0.getFullYear(), t0.getMonth() + 1, 0);
        const d1 = t1.getDay();
        const w1 = timeWeek.count(timeYear(t1), t1);

        return `
            M${(w0 + 1) * CELL_SIZE},${d0 * CELL_SIZE}
            H${w0 * CELL_SIZE} V${7 * CELL_SIZE}
            H${w1 * CELL_SIZE} V${(d1 + 1) * CELL_SIZE}
            H${(w1 + 1) * CELL_SIZE} V0
            H${(w0 + 1) * CELL_SIZE}Z
        `;
    }

    const formatDay = timeFormat('%Y-%m-%d');
    const keyDateList = getKeyDates();

    const colorScale = scaleQuantile({
        domain: [65000, 150000],
        // range: ['#d01c8b', '#f1b6da', '#f7f7f7', '#b8e186', '#4dac26'] // Pink to Green
        range: ['#d7191c', '#fdae61', '#ffffbf', '#abd9e9', '#2c7bb6'] //Orange to blue
    })

    const L1Start = new Date("2020-03-20")
    const L1End = new Date("2020-05-10")
    const L2Start = new Date("2020-07-08")
    const L2End = new Date("2020-11-08")
    const marketTypes = ['Residential', 'Commercial', 'Large']

    function updateDate(newDate: string) {
        setActiveDate(newDate)
        if (activeDay === "") { props.setStoryLim(2) }
        let Data2020 = props.KeyDateData.filter((d: KeyDate) => { return (formatDay(d.Date) === newDate) })
        let dayName = Data2020[0].DayName
        setActiveDay(dayName)
        let Data2019 = props.KeyDateData.filter((d: KeyDate) => { return (d.DayName === dayName) && (d.Year === 2019) })
        setKeyDate2019(Data2020.concat(Data2019)[3].Date)
        setselectedDayData(Data2020.concat(Data2019))

        setxScale(() => scaleBand({ domain: marketTypes, range: xAxisRange }))
        setyScale(() => scaleLinear<number>({ domain: [0, 50000], nice: true, range: yAxisRange }))
    }

    function getKeyDates() {
        let dateArray = []
        for (var key in props.KeyDateData) {
            dateArray.push(formatDay(props.KeyDateData[key].Date))
        }
        return dateArray;
    }

    let rowStyle: CSSProperties = {
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
    }

    const legendGlyphSize = 15

    return (
        <div>
            <Grid container>
                {[2019, 2020].map(year => {
                    const firstDayOfYear = new Date(year, 0, 1);
                    const lastDayOfYear = new Date(year + 1, 0, 1);
                    const yearDays = timeDays(firstDayOfYear, lastDayOfYear);
                    const yearMonths = timeMonths(firstDayOfYear, lastDayOfYear);

                    return (
                        <>
                            <Grid item xs={12}>
                                <svg width={props.width} height={graphCanvasHeight} key={`year-${year}`}>
                                    <Group transform={`translate(${(props.width - CELL_SIZE * 70) / 2},0)`}>
                                        <text transform={`translate(-6,${CELL_SIZE * 3.5})rotate(-90)`} fontFamily="sans-serif" fontSize={15} textAnchor="middle" >
                                            {year}
                                        </text>

                                        <Group fill="none" stroke="#ccc">
                                            {yearDays.map(day => {
                                                const dayString = formatDay(day);
                                                const value = props.data.filter((d: DailyChange) => { return dayString === formatDay(d.Date) })[0].Consumption
                                                const rextX = timeWeek.count(timeYear(day), day) * CELL_SIZE
                                                const rectY = day.getDay() * CELL_SIZE
                                                let cellborder = "#fffff"
                                                let cellstrokewidth = 1
                                                let period = ""
                                                if (day >= L1Start && day <= L1End) {
                                                    cellborder = Line2019Color;
                                                    cellstrokewidth = 2;
                                                    period = " (Lockdown 1)";
                                                }
                                                else if (day >= L2Start && day <= L2End) {
                                                    cellborder = Line2020Color;
                                                    cellstrokewidth = 2;
                                                    period = " (Lockdown 2)"
                                                }
                                                return (
                                                    <>
                                                        <Tooltip title={`${moment(dayString).format("dddd D MMMM YYYY")}: ${value} MWh ${period}`} key={`${dayString}-Tooltip-box`}>
                                                            <rect
                                                                fill={colorScale(value)}
                                                                width={CELL_SIZE}
                                                                height={CELL_SIZE}
                                                                x={rextX}
                                                                y={rectY}
                                                                key={`day-rect-${dayString}`}
                                                                stroke={showLockdownPeriod ? cellborder : "#fffff"}
                                                                strokeWidth={showLockdownPeriod ? cellstrokewidth : 1}
                                                                className={"daySquare"}
                                                            />
                                                        </Tooltip>
                                                    </>
                                                )
                                            })}
                                        </Group>

                                        <Group fill="none" stroke="#000" strokeWidth={4}>
                                            {yearMonths.map(firstOfMonth => (
                                                <path d={pathMonth(firstOfMonth)} key={`month-path-${formatDay(firstOfMonth)}-${year}`} />
                                            ))}
                                        </Group>
                                    </Group>
                                </svg>
                            </Grid>
                        </>
                    )
                })}

                <Grid item xs={12}>
                    <ButtonGroup size="small">
                        {props.KeyDateData.map(event => {
                            if (event.Market === "Residential" && event.Date.getFullYear() === 2020) {
                                return (
                                    <Button variant={activeDate === formatDay(event.Date) ? "contained" : "outlined"} color="primary" onClick={(e) => { updateDate(formatDay(event.Date)) }}>{event.DayName}</Button>
                                )
                            }
                        })}
                    </ButtonGroup>
                </Grid>

                {activeDate !== "" &&
                    <Grid item xs={4} style={rowStyle}>
                        <text>{activeDay} 2019 - {moment(KeyDate2019).format("DD MMMM")}</text>
                        <svg width={detailCanvasWidth} height={detailCanvasHeight} id="Calendar2019Graphs">
                            <AxisBottom top={detailCanvasHeight - topPad} scale={xScale} />
                            <AxisLeft left={leftPad} scale={yScale} />
                            <GridRows scale={yScale} width={detailCanvasWidth - leftPad} height={detailCanvasHeight} stroke="#e0e0e0" left={leftPad} />

                            <Group id="2019Graph">
                                {selectedDayData.filter((d: KeyDate) => { return (d.Year === 2019) }).map((d: KeyDate) => {
                                    const barHeight = detailCanvasHeight - yScale(d.Consumption);
                                    const barX = xScale(d.Market);
                                    const barY = detailCanvasHeight - barHeight;
                                    let lineColor = ResidentialColor
                                    if (d.Market === "Commercial") lineColor = SMEColor
                                    else if (d.Market === "Residential") lineColor = ResidentialColor
                                    else if (d.Market === "Large") lineColor = LargeColor
                                    return (
                                        <Bar
                                            key={`bar-${d.Date}-${d.Market}-outline`}
                                            x={barX + 50}
                                            y={barY - topPad}
                                            width={50}
                                            height={barHeight}
                                            fill={lineColor}
                                        />
                                    )
                                })}
                            </Group>
                        </svg>
                    </Grid>
                }
                {activeDate !== "" &&
                    <Grid item xs={4} style={rowStyle}>
                        <text>{activeDay} 2020 - {moment(activeDate).format("DD MMMM")}</text>

                        <svg width={detailCanvasWidth} height={detailCanvasHeight} id="Calendar2020Graphs">
                            <AxisBottom top={detailCanvasHeight - topPad} scale={xScale} />
                            <AxisLeft left={leftPad} scale={yScale} />
                            <GridRows scale={yScale} width={detailCanvasWidth - leftPad} height={detailCanvasHeight} stroke="#e0e0e0" left={leftPad} />

                            <Group id="2019Graph">
                                {selectedDayData.filter((d: KeyDate) => { return (d.Year === 2020) }).map((d: KeyDate) => {
                                    const barHeight = detailCanvasHeight - yScale(d.Consumption);
                                    const barX = xScale(d.Market);
                                    const barY = detailCanvasHeight - barHeight;
                                    let lineColor = ResidentialColor
                                    if (d.Market === "Commercial") lineColor = SMEColor
                                    else if (d.Market === "Residential") lineColor = ResidentialColor
                                    else if (d.Market === "Large") lineColor = LargeColor
                                    return (
                                        <Bar
                                            key={`bar-${d.Date}-${d.Market}-outline`}
                                            x={barX + 50}
                                            y={barY - topPad}
                                            width={50}
                                            height={barHeight}
                                            fill={lineColor}
                                        />
                                    )
                                })}
                            </Group>
                        </svg>
                    </Grid>
                }
                <Group id="CalLegend" style={{ position: 'fixed', right: '10%' }}>
                    <span>Daily Consumption (MWh)</span>
                    <LegendQuantile scale={colorScale}>
                        {(labels: any) =>
                            labels.reverse().map((label: any, i: number) => (
                                <LegendItem key={`legend-${i}`} >
                                    <svg width={legendGlyphSize} height={legendGlyphSize} style={{ margin: '2px 0' }}>
                                        <circle fill={label.value} r={legendGlyphSize / 2} cx={legendGlyphSize / 2} cy={legendGlyphSize / 2} />
                                    </svg>
                                    <LegendLabel align="left" margin="0 4px">
                                        {label.index === 0 && `Below ${label.extent[1]}`}
                                        {(label.index !== 0 && label.index !== 4) && `${Math.round(label.extent[0])} to ${Math.round(label.extent[1])}`}
                                        {label.index === 4 && `Above ${label.extent[0]}`}
                                    </LegendLabel>
                                </LegendItem>
                            ))
                        }
                    </LegendQuantile>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showLockdownPeriod}
                                onChange={(e) => { setshowLockdownPeriod(e.target.checked) }}
                                name="checkedB"
                                color="primary"
                            />
                        }
                        label="Show Lockdown Period"
                    />
                </Group>
            </Grid>

        </div >
    )
}
